// src/hooks/useLoad.tsx
import { useEffect, useState } from "react"
import { getMyJailStatus } from "store/apis/jailAPI"
import { setIsLoaded } from "store/slices/appSlice.ts"
import { fetchServerTime } from "store/apis/serverTimeAPI"
import { setErrorMessage } from "store/slices/commonSlice.ts"
import { getUserProgressMineGame } from "store/apis/mineGameAPI/mine-game.api.ts"
import {
  getAllLocations,
  getLocationUserProgress
} from "store/apis/locationAPI/location.api.ts"
import {
  getUserData,
  getUserGameStatistics,
  getUserSettings,
  setLastOpenApp
} from "store/apis/userAPI"
import { useModals } from "context/modals/hooks/useModals"
import { clearLocalStorage } from "utils/clearLocalStorage"
import { useAppDispatch } from "@/store"
import { useCheckActionOnLoad } from "./useCheckActionOnLoad"

export const useLoad = () => {
  const dispatch = useAppDispatch()

  const { openModal } = useModals()
  const {
    check: checkActionOnLoad,
    isTutorialComplete,
    isNoReadPolicy
  } = useCheckActionOnLoad()

  const [progress, setProgress] = useState<number>(0)

  const updateProgressOnResolve = (
    promise: Promise<any>,
    increment: number
  ) => {
    return promise.finally(() => {
      setProgress((prev) => Math.min(100, +(prev + increment).toFixed()))
    })
  }

  const bootstrap = async () => {
    // Auth is handled by authSlice before useLoad runs.
    // At this point status === 'authenticated' is guaranteed by AuthGuard.
    try {
      await dispatch(getAllLocations.initiate())
      await dispatch(getUserData.initiate()).unwrap().catch(clearLocalStorage)

      setProgress(20)

      const actions: Promise<any>[] = [
        checkActionOnLoad(),
        dispatch(fetchServerTime.initiate()),
        dispatch(getMyJailStatus.initiate()),
        dispatch(getLocationUserProgress.initiate()),
        dispatch(getUserProgressMineGame.initiate()),
        dispatch(getUserGameStatistics.initiate()),
        dispatch(getUserSettings.initiate()),
        dispatch(setLastOpenApp.initiate())
      ]

      const increment = 70 / actions.length

      await Promise.all(
        actions.map(async (action) =>
          updateProgressOnResolve(action, increment)
        )
      )

      setProgress(90)
    } catch (e) {
      openModal("globalError")
      dispatch(setErrorMessage(e))
    } finally {
      setProgress(100)
      setTimeout(() => {
        dispatch(setIsLoaded(true))

        setTimeout(() => {
          if (!isTutorialComplete.current && !isNoReadPolicy.current) {
            openModal("onboarding")
          }
        }, 200)
      }, 100)
    }
  }

  useEffect(() => {
    document.querySelector(".init-loader")?.remove()
    void bootstrap()
  }, [])

  return progress
}
